import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/Layout/LanguageSwitcher';
import RichTextContent from '../components/RichTextContent';
import WhatsAppIcon from '../components/UI/WhatsAppIcon';
import { trackPixelEvent } from '../components/MetaPixel';
import {
  getAvatarDisplayName,
  getAvatarHeadline,
  getAvatarBodyHtml,
  getAvatarCtaLabel
} from '../i18n/localizeContent';

interface AvatarData {
  _id: string;
  slug: string;
  photoUrl?: string;
  video?: { source: string; url: string };
  whatsappGroupUrl: string;
  displayName?: string;
  headline?: string;
  bodyHtml?: string;
  ctaLabel?: string;
  i18n?: any;
}

function readPreload(): { locale?: string; avatar?: AvatarData; notFound?: boolean; attribution?: any } | null {
  try {
    const el = document.getElementById('landing-preload');
    if (!el || !el.textContent) return null;
    return JSON.parse(el.textContent);
  } catch (_err) {
    return null;
  }
}

function isYouTubeOrVimeo(url: string) {
  return /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
}

const AvatarPageView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n: i18next } = useTranslation();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<AvatarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const currentLang = (i18next.language?.split('-')[0] || 'pt') as 'pt' | 'en' | 'es';

  useEffect(() => {
    const preload = readPreload();
    if (preload && preload.avatar && preload.locale === currentLang) {
      setData(preload.avatar);
      setLoading(false);
      return;
    }
    if (preload?.notFound) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    axios.get(`/avatars/public/${slug}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, currentLang]);

  const handleWhatsappClick = () => {
    if (!data) return;
    const utm = {
      source: searchParams.get('utm_source') || '',
      medium: searchParams.get('utm_medium') || '',
      campaign: searchParams.get('utm_campaign') || '',
      term: searchParams.get('utm_term') || '',
      content: searchParams.get('utm_content') || ''
    };
    axios.post(`/avatars/${data.slug}/click`, {
      lp: searchParams.get('lp') || '',
      utm,
      fbclid: searchParams.get('fbclid') || '',
      gclid: searchParams.get('gclid') || '',
      referrer: document.referrer,
      locale: currentLang
    }).catch(() => {});
    trackPixelEvent('Contact', { content_name: data.slug });
    // O clique é registrado via beacon assíncrono acima; o próprio <a href> nativo já navega
    // para o link do WhatsApp (target=_blank), sem precisar bloquear a navegação para esperar.
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('avatarPage.notFound')}</h1>
        <p className="text-gray-600">{t('avatarPage.notFoundDesc')}</p>
      </div>
    );
  }

  const displayName = getAvatarDisplayName(data, currentLang);
  const headline = getAvatarHeadline(data, currentLang);
  const bodyHtml = getAvatarBodyHtml(data, currentLang);
  const ctaLabel = getAvatarCtaLabel(data, currentLang) || t('avatarPage.defaultCta');

  return (
    <div className="min-h-screen hero-gradient">
      <div className="fixed top-4 right-4 z-40">
        <LanguageSwitcher compact variant="floating" />
      </div>

      <div className="container-custom max-w-xl section-padding text-center">
        {data.video?.url ? (
          isYouTubeOrVimeo(data.video.url) ? (
            <div className="aspect-video w-full mb-6 rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={data.video.url}
                title={displayName}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <video src={data.video.url} controls className="w-full mb-6 rounded-2xl shadow-2xl" />
          )
        ) : data.photoUrl ? (
          <img
            src={data.photoUrl}
            alt={displayName}
            className="w-32 h-32 mx-auto mb-6 rounded-full object-cover shadow-xl border-4 border-white"
          />
        ) : null}

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{displayName}</h1>
        {headline && <p className="text-lg text-gray-600 mb-6">{headline}</p>}

        {bodyHtml && <RichTextContent html={bodyHtml} className="text-center mb-8 text-gray-700 [&_ul]:list-inside [&_ol]:list-inside [&_ul]:pl-0 [&_ol]:pl-0" />}

        <a
          href={data.whatsappGroupUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleWhatsappClick}
          className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-[#25D366] hover:bg-[#1EBE5A] text-white font-semibold text-lg px-8 py-4 shadow-xl hover:shadow-2xl hover:scale-[1.03] transition-all duration-200"
        >
          <WhatsAppIcon className="w-6 h-6 shrink-0" />
          {ctaLabel}
        </a>
      </div>
    </div>
  );
};

export default AvatarPageView;
